import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";

import { useAuth } from "auth";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { withLocale } from "lib/i18n/path";

import { useSiteEntitlements } from "./site.hooks";
import { useSiteTranslations } from "./use-site-translations";

export function SiteLoadingCard() {
  return (
    <Card>
      <SoftBox py={8} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress color="info" />
      </SoftBox>
    </Card>
  );
}

export function SiteMessageCard({ children }: { children: ReactNode }) {
  return (
    <Card>
      <SoftBox p={3}>
        <SoftTypography variant="button" color="text" fontWeight="regular">
          {children}
        </SoftTypography>
      </SoftBox>
    </Card>
  );
}

// Every "Мій сайт" page: the dashboard chrome, and the content only for an Account whose plan
// includes a site (like the app's Profile menu row) with an active Profile.
export function SiteLayout({
  children,
}: {
  children: (context: { profileUuid: string; aiDesigner: boolean }) => ReactNode;
}) {
  const { activeProfileUuid, isProfilesLoading } = useAuth();
  const { isLoading, isSiteAvailable, aiDesigner } = useSiteEntitlements();
  const { locale, t } = useSiteTranslations();

  if (!isLoading && !isSiteAvailable) {
    return <Navigate to={withLocale("/dashboard", locale)} replace />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mt={4} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {isLoading || isProfilesLoading ? (
              <SiteLoadingCard />
            ) : !activeProfileUuid ? (
              <SiteMessageCard>{t.profileMissing}</SiteMessageCard>
            ) : (
              children({ profileUuid: activeProfileUuid, aiDesigner })
            )}
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}
